import '@data/Simulations/pipeline/blocks/CloseToProjSketchBlock';
import type { CloseToProjectSketchBlockParams } from '@data/Simulations/pipeline/blocks/CloseToProjSketchBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import RangeSlider from '@gui/components/generic/RangeSider';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import type { BlockParameters } from '@visualizer/types';
import Grid from 'Geodata/grid';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type CloseToProjectSketchParametersProps = {
    blockId: string;
};

const SLIDER_MAX_DISTANCE = 20;

const getDefaultRange = (): [number, number] => {
    const gridRes = typeof Grid.gridRes === 'number' && Grid.gridRes > 0 ? Grid.gridRes : SLIDER_MAX_DISTANCE;
    const max = Math.min(SLIDER_MAX_DISTANCE, gridRes) || SLIDER_MAX_DISTANCE;
    return [0, max] as [number, number];
};

export const getCloseToProjectSketchDefaultParams = (): Pick<
    CloseToProjectSketchBlockParams,
    'minValue' | 'maxValue' | 'distanceToRoof' | 'distanceToWall' | 'distanceToEdges'
> => {
    const [minValue, maxValue] = getDefaultRange();
    const [distanceToWall, distanceToRoof, distanceToEdges] = [true, true, true];
    return { minValue, maxValue, distanceToRoof, distanceToWall, distanceToEdges };
};

const CloseToProjectSketchParameters: FC<CloseToProjectSketchParametersProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<CloseToProjectSketchBlockParams>(blockId);
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const updateParams = (partial: Partial<CloseToProjectSketchBlockParams>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    const defaultRange = getDefaultRange();

    useEffect(() => {
        if (!params) {
            updateParams({
                minValue: defaultRange[0],
                maxValue: defaultRange[1],
            });
            return;
        }

        const missing: Partial<CloseToProjectSketchBlockParams> = {};
        if (params.minValue == null) {
            missing.minValue = defaultRange[0];
        }
        if (params.maxValue == null) {
            missing.maxValue = defaultRange[1];
        }
        if (params.distanceToRoof == null) {
            missing.distanceToRoof = true;
        }
        if (params.distanceToWall == null) {
            missing.distanceToWall = true;
        }
        if (params.distanceToEdges == null) {
            missing.distanceToEdges = true;
        }

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params]);

    const currentRange: [number, number] = [params?.minValue ?? defaultRange[0], params?.maxValue ?? defaultRange[1]];

    const handleRangeChange = (next: [number, number]) => {
        const [minValue, maxValue] = next;
        updateParams({ minValue, maxValue });
    };

    const roofChecked = params?.distanceToRoof ?? false;
    const wallChecked = params?.distanceToWall ?? false;
    const edgesChecked = params?.distanceToEdges ?? false;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.7 }}>
                <RangeSlider
                    value={currentRange}
                    lastIndex={SLIDER_MAX_DISTANCE}
                    showValues
                    size="small"
                    step={0.1}
                    onChange={handleRangeChange}
                    sx={{ ml: '5px', mr: '10px', flex: 1, minWidth: 0 }}
                />
                <Box sx={{ mr: 1 }}>[m]</Box>
            </Box>
            <Box sx={{ ml: 1 }}>
                <FormControlLabel
                    label="Roof"
                    sx={{
                        mr: 2.5,
                        '& .MuiCheckbox-root': {
                            p: 0.25,
                        },
                    }}
                    control={
                        <Checkbox size="small" checked={roofChecked} onChange={(e) => updateParams({ distanceToRoof: e.target.checked })} />
                    }
                />
                <FormControlLabel
                    label="Wall"
                    sx={{
                        mr: 2.5,
                        '& .MuiCheckbox-root': {
                            p: 0.25,
                        },
                    }}
                    control={
                        <Checkbox size="small" checked={wallChecked} onChange={(e) => updateParams({ distanceToWall: e.target.checked })} />
                    }
                />
                <FormControlLabel
                    label="Edges"
                    sx={{
                        mr: 2.5,
                        '& .MuiCheckbox-root': {
                            p: 0.25,
                        },
                    }}
                    control={
                        <Checkbox
                            size="small"
                            checked={edgesChecked}
                            onChange={(e) => updateParams({ distanceToEdges: e.target.checked })}
                        />
                    }
                />
            </Box>
        </Box>
    );
};

export default CloseToProjectSketchParameters;
